import React, { useState, useRef, useEffect } from 'react';
import { FaPlus, FaTags, FaSpinner, FaTimes, FaCheck } from 'react-icons/fa';
import TagBadge from './TagBadge';
import { useClientTypeAssignments } from '../../hooks/useClientTypeTags';
import styles from './ClientTypeTagManager.module.css';

/**
 * Enhanced component for managing client type tags with add/remove functionality
 * @param {Object} props - Component props
 * @param {number} props.clientId - Client ID
 * @param {string} [props.clientName] - Client name for display
 * @param {Array} [props.availableClientTypes] - Available client types for assignment
 * @param {Array} [props.preloadedAssignments] - Pre-loaded client type assignments to avoid API calls
 * @param {string} [props.size='medium'] - Size of tag badges
 * @param {number} [props.maxVisible=3] - Maximum number of tags to show before "show more"
 * @param {Function} [props.onUpdate] - Callback when tags are updated
 * @param {string} [props.emptyText='No types assigned'] - Text to show when no tags
 * @param {boolean} [props.compact=false] - Compact display mode
 * @param {boolean} [props.readOnly=false] - Read-only mode (no add/remove buttons)
 * @param {boolean} [props.localOnly=false] - Local-only mode (changes stored locally, no API calls)
 */
const ClientTypeTagManager = ({
    clientId,
    clientName,
    availableClientTypes = [],
    preloadedAssignments = null, // If provided, skip API calls and use this data
    size = 'medium',
    maxVisible = 3,
    onUpdate,
    emptyText = 'No types assigned',
    compact = false,
    readOnly = false,
    localOnly = false
}) => {
    // Local-only state for edit forms (no API calls)
    const [localAssignments, setLocalAssignments] = useState(preloadedAssignments || []);
    
    // Store initial preloadedAssignments ref to prevent unnecessary hook updates
    const initialPreloadedRef = useRef(preloadedAssignments);
    
    // Only use hook when NOT in local-only mode
    // In localOnly mode, we use the initial preloaded assignments to prevent hook from updating
    const hookResult = useClientTypeAssignments(
        clientId, 
        localOnly ? initialPreloadedRef.current : preloadedAssignments
    );

    // Use local state if localOnly, otherwise use hook
    const assignments = localOnly ? localAssignments : hookResult.assignments;
    const loading = localOnly ? false : hookResult.loading;
    const error = localOnly ? null : hookResult.error;
    const count = assignments.length;

    const [showAll, setShowAll] = useState(false);
    const [showAddDropdown, setShowAddDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    
    const dropdownRef = useRef(null);
    const addButtonRef = useRef(null);
    const initialAssignmentsRef = useRef(null);

    // Update local assignments when preloadedAssignments change (for localOnly mode)
    useEffect(() => {
        if (localOnly && preloadedAssignments) {
            setLocalAssignments(preloadedAssignments);
        }
    }, [localOnly, preloadedAssignments]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                addButtonRef.current && !addButtonRef.current.contains(event.target)) {
                setShowAddDropdown(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Track when assignments change and notify parent (NOT in local-only mode and NOT in readOnly mode)
    useEffect(() => {
        // Skip this effect entirely in local-only mode or readOnly mode - we handle updates manually or not at all
        if (localOnly || readOnly) return;
        
        // Skip the initial load
        if (initialAssignmentsRef.current === null) {
            initialAssignmentsRef.current = assignments;
            return;
        }
        
        // Only call onUpdate if assignments actually changed
        if (JSON.stringify(initialAssignmentsRef.current) !== JSON.stringify(assignments)) {
            initialAssignmentsRef.current = assignments;
            onUpdate?.(assignments);
        }
    }, [assignments, onUpdate, localOnly, readOnly]);

    // Get assigned client type IDs for filtering
    const assignedTypeIds = assignments.map(a => a.clientTypeId);
    
    // Filter available client types that aren't already assigned
    const unassignedTypes = availableClientTypes.filter(type => 
        !assignedTypeIds.includes(type.clientTypeId) &&
        (!searchTerm || type.clientType.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleAddTag = async (clientTypeId) => {
        if (actionLoading) return;
        
        setActionLoading(clientTypeId);
        try {
            if (localOnly) {
                // Local-only mode: just update local state
                const clientType = availableClientTypes.find(t => t.clientTypeId === clientTypeId);
                if (clientType) {
                    const newAssignment = {
                        clientTypeId: clientType.clientTypeId,
                        clientType: clientType.clientType,
                        clientTypeDescription: clientType.clientTypeDescription,
                        displayName: clientType.clientType,
                        displayDescription: clientType.clientTypeDescription
                    };
                    const updatedAssignments = [...localAssignments, newAssignment];
                    setLocalAssignments(updatedAssignments);
                    onUpdate?.(updatedAssignments);
                }
                setShowAddDropdown(false);
                setSearchTerm('');
            } else {
                // Normal mode: use hook to save to API
                await hookResult.addSingleAssignment(clientTypeId);
                setShowAddDropdown(false);
                setSearchTerm('');
            }
        } catch (error) {
            console.error('Failed to add tag:', error);
        } finally {
            setActionLoading(null);
        }
    };

    const handleRemoveTag = async (clientTypeId) => {
        if (actionLoading) return;
        
        setActionLoading(clientTypeId);
        try {
            if (localOnly) {
                // Local-only mode: just update local state
                const updatedAssignments = localAssignments.filter(a => a.clientTypeId !== clientTypeId);
                setLocalAssignments(updatedAssignments);
                onUpdate?.(updatedAssignments);
            } else {
                // Normal mode: use hook to save to API
                await hookResult.removeSingleAssignment(clientTypeId);
            }
        } catch (error) {
            console.error('Failed to remove tag:', error);
        } finally {
            setActionLoading(null);
        }
    };

    const visibleTags = showAll ? assignments : assignments.slice(0, maxVisible);
    const hiddenCount = Math.max(0, assignments.length - maxVisible);

    if (loading && assignments.length === 0) {
        return (
            <div className={`${styles.container} ${compact ? styles.compact : ''}`}>
                <div className={styles.loadingContainer}>
                    <FaSpinner className={styles.spinner} />
                    <span>Loading tags...</span>
                </div>
            </div>
        );
    }

    if (error && assignments.length === 0 && !localOnly) {
        return (
            <div className={`${styles.container} ${compact ? styles.compact : ''}`}>
                <div className={styles.errorContainer}>
                    <span className={styles.errorText}>Failed to load tags</span>
                    <button 
                        type="button"
                        className={styles.retryButton}
                        onClick={hookResult.refreshAssignments}
                        disabled={loading}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.container} ${compact ? styles.compact : ''}`}>
            {/* Header with count and add button */}
            <div className={styles.header}>
                <div className={styles.headerInfo}>
                    <FaTags className={styles.headerIcon} />
                    {!compact && (
                        <span className={styles.headerText}>
                            {count > 0 ? `${count} type${count !== 1 ? 's' : ''}` : 'Client Types'}
                        </span>
                    )}
                </div>
                
                {!readOnly && (
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            ref={addButtonRef}
                            className={styles.addButton}
                            onClick={() => setShowAddDropdown(!showAddDropdown)}
                            disabled={loading || actionLoading}
                            title="Add client type"
                        >
                            <FaPlus />
                            {!compact && <span>Add</span>}
                        </button>
                    </div>
                )}

                {/* Add dropdown */}
                {showAddDropdown && (
                    <div ref={dropdownRef} className={styles.addDropdown}>
                        <div className={styles.dropdownHeader}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search client types..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                            <button
                                type="button"
                                className={styles.closeButton}
                                onClick={() => {
                                    setShowAddDropdown(false);
                                    setSearchTerm('');
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className={styles.dropdownContent}>
                            {unassignedTypes.length === 0 ? (
                                <div className={styles.dropdownEmpty}>
                                    {searchTerm ? 'No matching client types found' : 'All available types are already assigned'}
                                </div>
                            ) : (
                                <div className={styles.dropdownList}>
                                    {unassignedTypes.map(type => (
                                        <button
                                            type="button"
                                            key={type.clientTypeId}
                                            className={styles.dropdownItem}
                                            onClick={() => handleAddTag(type.clientTypeId)}
                                            disabled={actionLoading === type.clientTypeId}
                                        >
                                            <span className={styles.typeText}>{type.clientType}</span>
                                            {actionLoading === type.clientTypeId ? (
                                                <FaSpinner className={styles.actionSpinner} />
                                            ) : (
                                                <FaPlus className={styles.addIcon} />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Tags display */}
            <div className={styles.tagsDisplay}>
                {assignments.length === 0 ? (
                    <span className={styles.emptyText}>{emptyText}</span>
                ) : (
                    <>
                        <div className={styles.tagsList}>
                            {visibleTags.map((tag) => (
                                <TagBadge
                                    key={tag.clientTypeId}
                                    tag={tag}
                                    size={size}
                                    removable={!readOnly}
                                    onRemove={handleRemoveTag}
                                    disabled={actionLoading === tag.clientTypeId}
                                    loading={actionLoading === tag.clientTypeId}
                                />
                            ))}
                        </div>
                        
                        {hiddenCount > 0 && (
                            <button
                                type="button"
                                className={styles.showMoreButton}
                                onClick={() => setShowAll(!showAll)}
                                disabled={loading || actionLoading}
                            >
                                {showAll ? 'Show less' : `+${hiddenCount} more`}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ClientTypeTagManager;
